import React, { useState } from 'react';
import { Settings, Shield, Users, CheckCircle, Check, Calculator } from 'lucide-react';

const SolutionSection = () => {
  const [activeFeature, setActiveFeature] = useState(0);
  const [selectedConfiguration, setSelectedConfiguration] = useState({
    width: 3,
    height: 2.5,
    glazingType: 'double',
    frameColor: 'white'
  });
  const [imageTransition, setImageTransition] = useState(null);

  const solutions = [
    { id: 1, title: 'Hliníkové posuvné systémy', description: 'Prémiové hliníkové rámy s posuvnými panelmi pre maximálnu funkčnosť', features: ['Bezúdržbové hliníkové profily', 'Plynulé posúvanie', 'Moderný dizajn', 'Dlhá životnosť'], image: "../../../assets/images/photos/123.webp", imageAlt: 'Modern aluminum sliding glazing system with sleek profiles on balcony', icon: Settings, color: 'bg-blue-500' },
    { id: 2, title: 'Bezpečnostné sklo', description: 'Laminované a tvrdené sklo pre maximálnu bezpečnosť a izoláciu', features: ['Laminované bezpečnostné sklo', 'UV ochrana', 'Tepelná izolácia', 'Zvuková izolácia'], image: "../../../assets/images/photos/124.webp", imageAlt: 'Safety laminated glass panels providing UV protection and insulation', icon: Shield, color: 'bg-green-500' },
    { id: 3, title: 'Profesionálna inštalácia', description: 'Certifikovaní technici s 24-ročnou skúsenosťou v zasklievaní', features: ['Presné zameranie', 'Rýchla inštalácia', 'Čistota práce', 'Záruka kvality'], image: "../../../assets/images/photos/125.webp", imageAlt: 'Professional installation team working on balcony glazing with precision tools', icon: Users, color: 'bg-orange-500' }
  ];

  const glazingTypes = [
    { id: 'single',  name: 'Jedno',  price: 45, description: 'Základná' },
    { id: 'double',  name: 'Dvoj',   price: 65, description: 'Lepšia'   },
    { id: 'triple',  name: 'Troj',   price: 85, description: 'Max'      }
  ];

  const frameColors = [
    { id: 'white', name: 'Biela', hex: '#FFFFFF', images: { single: '../../../assets/images/kofiguration/однокамерный.webp', double: '../../../assets/images/kofiguration/двукамерный.png', triple: '../../../assets/images/kofiguration/трехкамерный.webp' } },
    { id: 'orange', name: 'hnedý', hex: '#371B00', images: { single: '../../../assets/images/kofiguration/brown-однокамерный.webp', double: '../../../assets/images/kofiguration/brown-двукамерная.webp', triple: '../../../assets/images/kofiguration/bown-трехкамерный.webp' } },
    { id: 'anthracite', name: 'Antracit', hex: '#2F4F4F', images: { single: '../../../assets/images/kofiguration/antracit-однокамерный.webp', double: '../../../assets/images/kofiguration/antracit-двукамерная.webp', triple: '../../../assets/images/kofiguration/antracit-трехкамерный.webp' } },
    { id: 'green', name: 'Zelená', hex: '#228B22', images: { single: '../../../assets/images/kofiguration/green-однокамерный.webp', double: '../../../assets/images/kofiguration/green-двукамерная.webp', triple: '../../../assets/images/kofiguration/green-трехкамерный.webp' } }
  ];

  const calculatePrice = () => {
    const area = selectedConfiguration.width * selectedConfiguration.height;
    const glazingPrice = glazingTypes.find(t => t.id === selectedConfiguration.glazingType)?.price || 65;
    return Math.round(area * glazingPrice);
  };

  const handleQuoteClick = () => alert('Presmerovanie na formulár pre cenovú ponuku');

  const handleGlazingChange = (newType) => {
    if (newType !== selectedConfiguration.glazingType) {
      setImageTransition('slide');
      setTimeout(() => {
        setSelectedConfiguration(prev => ({ ...prev, glazingType: newType }));
        setImageTransition(null);
      }, 500);
    }
  };

  const handleColorChange = (newColor) => {
    if (newColor !== selectedConfiguration.frameColor) {
      setImageTransition('slide');
      setTimeout(() => {
        setSelectedConfiguration(prev => ({ ...prev, frameColor: newColor }));
        setImageTransition(null);
      }, 500);
    }
  };

  const currentColor = frameColors.find(c => c.id === selectedConfiguration.frameColor);
  const currentImage = currentColor?.images[selectedConfiguration.glazingType];
  const currentLabel = `${glazingTypes.find(t => t.id === selectedConfiguration.glazingType)?.name} - ${currentColor?.name}`;

  return (
    <div className="w-full">
      {/* ===== CONFIG HERO (адаптив) ===== */}
      <section className="relative w-full bg-white">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 items-center gap-6 md:gap-8 min-h-[56vh] md:min-h-[60vh] lg:min-h-[80vh] px-4 py-8 md:py-10">
          
          {/* IMAGE (сверху на мобиле) */}
          <div className="order-1">
            <div className="relative rounded-2xl overflow-hidden shadow-xl border border-gray-200 bg-white">
              <img
                src={currentImage}
                alt={currentLabel}
                className={[
                  'w-full h-[42vh] sm:h-[50vh] lg:h-[70vh]',
                  'object-contain',
                  'transition-all duration-500 ease-in-out',
                  imageTransition === 'slide' ? 'translate-x-full opacity-0' : 'translate-x-0 opacity-100'
                ].join(' ')}
              />
              <div className="absolute left-3 top-3">
                <span className="inline-flex items-center gap-2 bg-green-50 text-green-700 border border-green-200 px-3 py-1.5 rounded-full text-xs font-semibold">
                  <CheckCircle size={16} />
                  Naše riešenie
                </span>
              </div>
            </div>

            {/* Селекторы под картинкой (только ≤ lg) */}
            <div className="mt-4 lg:hidden">
              {/* Zasklenie */}
              <p className="text-sm font-semibold text-gray-900 mb-2">Zasklenie:</p>
              <div className="grid grid-cols-3 gap-2 mb-4">
                {glazingTypes.map((type) => {
                  const active = selectedConfiguration.glazingType === type.id;
                  return (
                    <button
                      key={type.id}
                      onClick={() => handleGlazingChange(type.id)}
                      disabled={imageTransition === 'slide'}
                      className={`py-2 px-3 rounded-lg text-xs font-semibold transition-colors disabled:opacity-50 ${
                        active ? 'bg-blue-600 text-white shadow' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      <div className={active ? 'text-white' : 'text-inherit'}>{type.name}komorové</div>
                      <div className={`text-[11px] font-normal ${active ? 'text-white/90' : 'text-gray-700/80'}`}>{type.price}€/m²</div>
                    </button>
                  );
                })}
              </div>

              {/* Farba */}
              <p className="text-sm font-semibold text-gray-900 mb-2">Farba:</p>
              <div className="grid grid-cols-4 gap-2">
                {frameColors.map((color) => {
                  const active = selectedConfiguration.frameColor === color.id;
                  return (
                    <button
                      key={color.id}
                      onClick={() => handleColorChange(color.id)}
                      disabled={imageTransition === 'slide'}
                      className={`py-3 px-2 rounded-lg transition-all flex flex-col items-center gap-1.5 disabled:opacity-50 ${
                        active ? 'bg-blue-600 text-white shadow' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      <div
                        className={`w-7 h-7 rounded-full transition-all ${active ? 'ring-2 ring-white' : 'ring-1 ring-gray-300'}`}
                        style={{ backgroundColor: color.hex }}
                      />
                      <span className={`text-xs ${active ? 'text-white' : 'text-gray-700'}`}>{color.name}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* CONFIG CARD (снизу на мобиле, справа на десктопе) */}
          <div className="order-2 w-full flex justify-center items-center">
            <div className="w-full max-w-md">
              {/* Title */}
              <div className="mb-5 md:mb-6 lg:mb-8 text-center">
                <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-1.5 sm:mb-2">
                  Kompletné zasklenie balkónov
                </h1>
                <p className="text-sm sm:text-base text-gray-600">
                  Transformujeme váš balkón na celoročne využiteľný priestor
                </p>
              </div>

              {/* Card */}
              <div className="bg-white rounded-2xl p-4 sm:p-5 border border-gray-200 shadow-xl mb-5 sm:mb-6">
                {/* Dimensions */}
                <div className="mb-4 sm:mb-5">
                  <h3 className="text-sm font-semibold text-gray-900 mb-3 sm:mb-4">Rozmery balkóna</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-2">
                        Šírka: <span className="text-lg font-bold text-gray-900">{selectedConfiguration.width}m</span>
                      </label>
                      <input
                        type="range"
                        min="2"
                        max="8"
                        step="0.5"
                        value={selectedConfiguration.width}
                        onChange={(e) => setSelectedConfiguration(prev => ({ ...prev, width: parseFloat(e.target.value) }))}
                        className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-2">
                        Výška: <span className="text-lg font-bold text-gray-900">{selectedConfiguration.height}m</span>
                      </label>
                      <input
                        type="range"
                        min="2"
                        max="3.5"
                        step="0.1"
                        value={selectedConfiguration.height}
                        onChange={(e) => setSelectedConfiguration(prev => ({ ...prev, height: parseFloat(e.target.value) }))}
                        className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
                      />
                    </div>
                  </div>
                </div>

                {/* Price */}
                <div className="bg-gray-50 rounded-xl p-3.5 sm:p-4 border border-gray-200">
                  <h3 className="text-sm font-semibold text-gray-900 mb-2.5 sm:mb-3">Orientačná cena</h3>
                  <div className="space-y-2 text-xs mb-3">
                    <div className="flex justify-between text-gray-700">
                      <span>Plocha:</span>
                      <span className="font-semibold">{(selectedConfiguration.width * selectedConfiguration.height).toFixed(1)} m²</span>
                    </div>
                    <div className="flex justify-between text-gray-700">
                      <span>Cena za m²:</span>
                      <span className="font-semibold">{glazingTypes.find(t => t.id === selectedConfiguration.glazingType)?.price}€</span>
                    </div>
                    <div className="border-t border-gray-200 pt-2">
                      <div className="flex justify-between items-center">
                        <span className="font-semibold text-gray-900">Celkom:</span>
                        <span className="text-2xl font-bold text-gray-900">{calculatePrice()}€</span>
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={handleQuoteClick}
                    className="w-full bg-blue-600 text-white px-4 py-2.5 rounded-lg font-bold text-sm hover:bg-blue-700 transition-colors flex items-center justify-center gap-2 shadow"
                  >
                    <Calculator size={18} />
                    Cena
                  </button>
                </div>

                {/* Current selection */}
                <div className="mt-4 text-center">
                  <div className="inline-flex items-center gap-2 bg-gray-100 px-4 py-2 rounded-full border border-gray-200 text-xs text-gray-800">
                    <span>{glazingTypes.find(t => t.id === selectedConfiguration.glazingType)?.name}</span>
                    <span className="text-gray-400">•</span>
                    <span className="font-semibold">{currentColor?.name}</span>
                  </div>
                </div>

                {/* ДЕСКТОП селекторы (только lg+) */}
                <div className="space-y-3 mt-6 hidden lg:block">
                  {/* Zasklenie */}
                  <div>
                    <p className="text-xs font-semibold text-gray-900 mb-2">Zasklenie:</p>
                    <div className="grid grid-cols-3 gap-2">
                      {glazingTypes.map((type) => {
                        const active = selectedConfiguration.glazingType === type.id;
                        return (
                          <button
                            key={type.id}
                            onClick={() => handleGlazingChange(type.id)}
                            disabled={imageTransition === 'slide'}
                            className={`py-2 px-3 rounded-lg text-xs font-semibold transition-colors disabled:opacity-50 ${
                              active ? 'bg-blue-600 text-white shadow' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }`}
                          >
                            <div className={active ? 'text-white' : 'text-inherit'}>{type.name}komorové</div>
                            <div className={`text-[11px] font-normal ${active ? 'text-white/90' : 'text-gray-700/80'}`}>{type.price}€/m²</div>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Farba */}
                  <div>
                    <p className="text-xs font-semibold text-gray-900 mb-2">Farba:</p>
                    <div className="grid grid-cols-4 gap-2">
                      {frameColors.map((color) => {
                        const active = selectedConfiguration.frameColor === color.id;
                        return (
                          <button
                            key={color.id}
                            onClick={() => handleColorChange(color.id)}
                            disabled={imageTransition === 'slide'}
                            className={`py-3 px-2 rounded-lg transition-all flex flex-col items-center gap-1.5 disabled:opacity-50 ${
                              active ? 'bg-blue-600 text-white shadow' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }`}
                          >
                            <div className={`w-7 h-7 rounded-full transition-all ${active ? 'ring-2 ring-white' : 'ring-1 ring-gray-300'}`} style={{ backgroundColor: color.hex }} />
                            <span className={`text-xs ${active ? 'text-white' : 'text-gray-700'}`}>{color.name}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>

              </div>
            </div>
          </div>

        </div>
      </section>

      {/* ===== Solution Features (адаптив по сетке) ===== */}
      <section className="bg-white py-14 md:py-24">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-8 sm:mb-10 md:mb-12 text-center">
            Prečo si vybrať nás
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {solutions.map((solution, index) => {
              const IconComponent = solution.icon;
              return (
                <div
                  key={solution.id}
                  className={`bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden transition-all duration-300 hover:shadow-2xl cursor-pointer transform hover:-translate-y-2 ${activeFeature === index ? 'ring-2 ring-blue-600' : ''}`}
                  onClick={() => setActiveFeature(index)}
                >
                  <div className="relative h-56 sm:h-64 md:h-72 overflow-hidden">
                    <img src={solution.image} alt={solution.imageAlt} className="w-full h-full object-cover transition-transform duration-500 hover:scale-110" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                    <div className={`absolute top-4 right-4 w-10 h-10 sm:w-12 sm:h-12 ${solution.color} rounded-full flex items-center justify-center text-white shadow-lg`}>
                      <IconComponent size={22} className="sm:hidden" />
                      <IconComponent size={24} className="hidden sm:inline" />
                    </div>
                  </div>
                  <div className="p-5 sm:p-6">
                    <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2.5">{solution.title}</h3>
                    <p className="text-gray-600 mb-4 leading-relaxed text-sm sm:text-base">{solution.description}</p>
                    <ul className="space-y-2">
                      {solution.features.map((feature, i) => (
                        <li key={i} className="flex items-center space-x-3">
                          <Check size={16} className="text-green-600 flex-shrink-0" />
                          <span className="text-sm text-gray-600">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
};

export default SolutionSection;